/* "Non trovo la prestazione": chi imputa la propone senza telefonare a nessuno.
 *
 * Dal 16 ottobre 2026. Il codice lo genera il programma: chi propone scrive solo come si chiama e di che
 * tipo di lavoro e'. Prima di creare si mostrano le prestazioni simili, perche' dieci persone che inventano
 * codici producono doppioni ("Dich. IVA", "Dichiarazione IVA", "IVA dichiarazione") che spezzano il controllo
 * per prestazione. Il codice nuovo vale subito, a ore e senza prezzo, e resta "da rivedere" finche' chi
 * fattura non ha scelto la frase che finisce in fattura: l'operatore non scrive mai cio' che legge il cliente.
 */

import { useState, type KeyboardEvent } from "react";
import { ErroreApi, anagrafica } from "../api";
import { TIPI_PRATICA, nomeTipo, type PrestazioneSimile, type TipoPratica } from "../dominio";

export function ProponiPrestazione({
  usa,
  dopo,
}: {
  /** Chiamata con il codice scelto: o quello esistente ("usa questa") o quello appena nato. */
  usa: (codice: string) => void;
  /** Ricarica il catalogo, cosi' il codice nuovo compare nell'elenco. */
  dopo: () => Promise<void>;
}) {
  const [aperto, setAperto] = useState(false);
  const [descrizione, setDescrizione] = useState("");
  const [tipo, setTipo] = useState<TipoPratica | "">("");
  const [simili, setSimili] = useState<PrestazioneSimile[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  function chiudi() {
    setAperto(false);
    setSimili(null);
    setErrore(null);
  }

  async function crea(diversa: boolean) {
    if (tipo === "") return;
    setErrore(null);
    setInCorso(true);
    try {
      const nuova = await anagrafica.proponi({ descrizione, tipo, diversa });
      await dopo();
      usa(nuova.codice);
      setFatto(`«${nuova.descrizione}» è nata e la puoi usare subito. Chi fattura la rivedrà.`);
      setDescrizione("");
      setTipo("");
      setSimili(null);
      setAperto(false);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a crearla.");
    } finally {
      setInCorso(false);
    }
  }

  /** Primo passo: si cercano le simili. Se non ce ne sono si crea subito. */
  async function controlla() {
    setErrore(null);
    setFatto(null);
    if (descrizione.trim().length < 5) return setErrore("Scrivi come si chiama la prestazione, almeno 5 caratteri.");
    if (tipo === "") return setErrore("Scegli il tipo di lavoro.");
    setInCorso(true);
    try {
      const trovate = await anagrafica.simili(descrizione, tipo);
      if (trovate.length === 0) {
        setInCorso(false);
        return await crea(false);
      }
      setSimili(trovate);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a controllare.");
    } finally {
      setInCorso(false);
    }
  }

  if (!aperto) {
    return (
      <div className="proponi-prestazione">
        {fatto && (
          <p className="riga-fatto" role="status">
            {fatto}
          </p>
        )}
        <button type="button" className="tasto tasto-nudo" onClick={() => setAperto(true)}>
          Non trovo la prestazione
        </button>
      </div>
    );
  }

  const identica = simili?.find((s) => s.identica && s.stesso_tipo);

  return (
    // Un <div> e non un <form>: questo blocco sta dentro il modulo delle ore, e un invio qui
    // farebbe partire anche l'imputazione. Invio nel campo del nome vale "Crea la prestazione".
    <div className="proponi-prestazione riquadro-leggero" role="group" aria-label="Nuova prestazione">
      <p className="narrativa">
        Prima guarda bene l'elenco. Se non c'è, scrivi come si chiama: la crei adesso e la usi subito, poi chi
        fattura la sistema.
      </p>
      <div className="imputa-riga">
        <div className="campo campo-largo">
          <label htmlFor="proponi-descrizione">Come si chiama</label>
          <input
            id="proponi-descrizione"
            value={descrizione}
            maxLength={300}
            placeholder="per esempio: verifica saldi di banca"
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void controlla();
              }
            }}
            onChange={(e) => {
              setDescrizione(e.target.value);
              setSimili(null);
            }}
          />
        </div>
        <div className="campo campo-largo">
          <label htmlFor="proponi-tipo">Di che tipo di lavoro è</label>
          <select
            id="proponi-tipo"
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value as TipoPratica | "");
              setSimili(null);
            }}
          >
            <option value="">Scegli</option>
            {TIPI_PRATICA.map((t) => (
              <option key={t.valore} value={t.valore}>
                {t.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      {simili !== null && (
        <div className="anteprima-tariffa" role="status">
          <h3>{identica ? "Esiste già" : "Ce n'è una simile"}</h3>
          <ul className="simili">
            {simili.map((s) => (
              <li key={s.codice}>
                <strong>{s.descrizione}</strong>
                <span className="narrativa">
                  {s.tipi_pratica.map((t) => nomeTipo(t as TipoPratica)).join(", ")}
                  {s.da_rivedere ? ", nuova" : ""}
                </span>
                {s.stesso_tipo ? (
                  <button
                    type="button"
                    className="tasto tasto-piccolo"
                    onClick={() => {
                      usa(s.codice);
                      chiudi();
                    }}
                  >
                    Usa questa
                  </button>
                ) : (
                  <span className="narrativa">non vale per questo tipo di lavoro</span>
                )}
              </li>
            ))}
          </ul>
          {!identica && (
            <div>
              <button type="button" className="tasto" disabled={inCorso} onClick={() => crea(true)}>
                {inCorso ? "Sto creando" : "La mia è un'altra cosa, creala"}
              </button>
            </div>
          )}
        </div>
      )}

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}

      <div>
        {simili === null && (
          <button className="tasto tasto-principale" type="button" disabled={inCorso} onClick={() => void controlla()}>
            {inCorso ? "Controllo" : "Crea la prestazione"}
          </button>
        )}{" "}
        <button type="button" className="tasto tasto-nudo" onClick={chiudi}>
          Lascia
        </button>
      </div>
    </div>
  );
}
